/**
 * Game Configuration
 * All game constants centralized in one place
 */

export const GameState = {
    BOOT: 'boot',
    TITLE: 'title',
    HONEYPOT: 'honeypot',
    TRANSITION: 'transition',
    GAMEPLAY: 'gameplay',
    GAME_OVER: 'game_over',
    VICTORY: 'victory'
};

export const CONSTANTS = {
    // ===================
    // GAME DIMENSIONS
    // ===================
    WIDTH: 800,
    HEIGHT: 600,

    // ===================
    // SCROLLING
    // ===================
    SCROLL_SPEED_BASE: 120,           // Initial scroll speed (pixels/second)
    SCROLL_SPEED_INCREASE: 0.05,      // 5% increase per interval
    SCROLL_SPEED_INTERVAL: 10000,     // Increase every 10 seconds
    SCROLL_SPEED_MAX: 300,            // Maximum scroll speed

    // ===================
    // PLAYER PHYSICS
    // ===================
    PLAYER_GRAVITY: 600,              // Constant pull down
    PLAYER_JUMP_FORCE: -250,          // Upward boost per tap
    PLAYER_MAX_FALL_SPEED: 400,       // Terminal velocity
    PLAYER_MAX_RISE_SPEED: -350,      // Max upward speed
    PLAYER_BASE_SCALE: 40 / 1080,     // Base size (~0.037)
    PLAYER_BASE_RADIUS: 20,           // Base collision radius
    PLAYER_MIN_SCALE: 0.8,            // Can't shrink below 80%
    PLAYER_MAX_SCALE: 3.0,            // Max bloat (overfit death)
    PLAYER_X_POSITION: 0.35,          // Player X as % of screen width
    PLAYER_INVINCIBILITY_TIME: 1000,  // ms of invincibility after hit

    // ===================
    // REGRESSION LINE
    // ===================
    // LINE_BASE_INTERCEPT removed - now uses dynamic screen center (scene.scale.height / 2)
    LINE_SLOPE_MIN: -0.7,             // Min slope (steeper = harder)
    LINE_SLOPE_MAX: 0.7,              // Max slope
    LINE_LERP_RATE: 0.1,              // How fast line responds to data
    LINE_VARIANCE_BASE: 0.1,
    LINE_VARIANCE_MAX: 0.5,

    // ===================
    // TOLERANCE ZONES
    // ===================
    ZONE_SAFE: 30,                    // No punishment within this range
    ZONE_DANGER: 70,                  // Moderate punishment
    ZONE_DEATH: 110,                  // Heavy punishment

    // ===================
    // ERROR SYSTEM
    // ===================
    ERROR_MAX: 100,
    ERROR_RECOVERY_SAFE: 0.1,         // Error recovery per frame in safe zone
    ERROR_RECOVERY_ONLINE: 0.02,      // Error recovery when on line
    ERROR_WARNING_RATE: 0.1,          // Max error per frame in warning zone
    ERROR_DANGER_BASE: 0.2,           // Base error in danger zone
    ERROR_DANGER_MAX: 0.3,            // Additional error at edge of danger
    ERROR_DEATH_RATE: 0.5,            // Error per frame in death zone
    ERROR_DATAPOINT_HIT: 10,          // Error when hitting data point
    ERROR_GHOST_PATH_DAMAGE: 5,
    ERROR_OUTLIER_DAMAGE: 15,
    ERROR_PATH_CROSS_DAMAGE: 10,

    // ===================
    // OVERFITTING (GROWTH)
    // ===================
    GROWTH_DATAPOINT: 0.2,            // Growth when hitting data point
    GROWTH_GHOST_PATH: 0.03,          // Growth per second on ghost path
    SHRINK_ON_LINE: 0.002,            // Shrink per frame when on line

    // ===================
    // DATA POINTS
    // ===================
    DATA_POINT_SPAWN_INTERVAL: 500,   // ms between spawns
    DATA_POINT_BASE_SPREAD: 200,      // Base Y scatter
    DATA_POINT_DIFFICULTY_SPREAD: 100,// Additional spread from difficulty
    DATA_POINT_OUTLIER_CHANCE: 0.15,  // Chance of extreme outlier (2x spread)

    // ===================
    // GHOST PATH
    // ===================
    GHOST_PATH_OFFSET_MAX: 80,
    GHOST_DREAD_THRESHOLD: 500,       // ms before dread triggers
    GHOST_DREAD_COOLDOWN: 3000,       // ms between dread events

    // ===================
    // QUANTUM SUPERPOSITION (Player ability)
    // ===================
    QUANTUM_CHARGE_TIME: 3000,        // 3 seconds on line to fully charge
    QUANTUM_COOLDOWN: 10000,          // 10 seconds before can charge again
    QUANTUM_EFFECT_DURATION: 500,     // Visual effect duration when triggered
    QUANTUM_GHOST_COUNT: 3,           // Number of ghost clones when triggered

    // ===================
    // BOSS FIGHT
    // ===================
    BOSS_TRIGGER_TIME: 60000,         // 60 seconds until boss

    // Phase durations
    BOSS_PHASE1_DURATION: 25000,      // 25 seconds
    BOSS_PHASE2_DURATION: 30000,      // 30 seconds
    BOSS_PHASE3_DURATION: 35000,      // 35 seconds

    // Boss health
    BOSS_MAX_HEALTH: 100,
    BOSS_DAMAGE_RATE: 0.015,          // Damage per frame when player on line

    // Attack intervals
    BOSS_ATTACK_INTERVAL_P1: 1200,    // Phase 1: slower
    BOSS_ATTACK_INTERVAL_P2: 1000,    // Phase 2: faster
    BOSS_ATTACK_INTERVAL_P3: 800,     // Phase 3: rapid

    // Projectile settings
    BOSS_PROJECTILE_SPEED: 280,       // Base projectile speed
    BOSS_PROJECTILE_SPEED_MIN: 250,   // Min random speed
    BOSS_PROJECTILE_SPEED_MAX: 400,   // Max random speed
    BOSS_PROJECTILE_BASE_RADIUS: 8,
    BOSS_PROJECTILE_MAX_RADIUS: 30,
    BOSS_PROJECTILE_LIFE: 6000,       // ms lifetime
    BOSS_PROJECTILE_HIT_GROWTH: 0.15, // Player growth when hit

    // Phase 3 positions
    BOSS_LEFT_LIMIT: 0.15,            // Left overfitter stops at 15% (wider gap)
    BOSS_RIGHT_LIMIT: 0.85,           // Right overfitter stops at 85% (wider gap)
    BOSS_PLAYER_CENTER: 0.50,         // Player forced to 50%

    // Line movement in Phase 2/3
    BOSS_LINE_SLOPE_AMPLITUDE: 0.2,   // How much line slope oscillates
    BOSS_LINE_OFFSET_AMPLITUDE: 100,  // How much line Y offset oscillates
    BOSS_LINE_LERP_RATE_P2: 0.025,    // Phase 2 lerp (medium)
    BOSS_LINE_LERP_RATE_P3: 0.020,     // Phase 3 lerp (harder)

    // ===================
    // OVERFITTER ENEMY
    // ===================
    OVERFITTER_DELAY: 2000,           // 2 second mimicry delay
    OVERFITTER_SCALE: 200 / 1080,     // Boss mode scale
    PATH_HISTORY_LIFETIME: 5000,
    PATH_HISTORY_INTERVAL: 100,

    // ===================
    // HONEYPOT PHASE
    // ===================
    HONEYPOT_DURATION: 5000,
    HONEYPOT_ANSWERS_THRESHOLD: 5,
    HONEYPOT_DREAD_START: 3000,

    // ===================
    // AUDIO
    // ===================
    HEARTBEAT_BPM_MIN: 60,
    HEARTBEAT_BPM_MAX: 180,

    // ===================
    // COLORS
    // ===================
    COLORS: {
        // Bright/honeypot phase
        WHITE: 0xFFFFFF,
        BRIGHT_BG: 0xF5F5F5,
        CHEERFUL_ACCENT: 0x4CAF50,
        BUTTON_GREEN: 0x4CAF50,
        BUTTON_HOVER: 0x66BB6A,

        // Dark/horror phase
        DARK_BG: 0x0a0a12,
        GRID_BG: 0x1a1a2e,
        GRID_LINE: 0x2a2a4e,

        // Quantato
        QUANTATO_HAPPY: 0xFFD93D,
        QUANTATO_HORROR: 0x4A0E0E,
        QUANTATO_CHEEK: 0xFFB6C1,

        // Eye colors
        EYE_SCLERA: 0xFFFAF0,
        EYE_IRIS: 0x4A7C59,
        EYE_PUPIL: 0x000000,
        EYE_HIGHLIGHT: 0xFFFFFF,
        EYE_BLOODSHOT: 0xCC4444,

        // Line colors
        LINE_TRUE: 0xFFFFFF,
        LINE_GHOST: 0x8888FF,
        LINE_GLOW: 0xFFFFFF,

        // Path history
        PATH_DANGER: 0xFF0000,

        // Data points
        DATA_CORRECT: 0x00FF00,
        DATA_OUTLIER: 0xFF0000,

        // Overfitter
        OVERFITTER_SHADOW: 0x1a0a0a,

        // Boss projectiles
        PROJECTILE_LEFT: 0xFF4444,    // Red/orange
        PROJECTILE_RIGHT: 0x8844FF,   // Purple/blue

        // Error/UI
        ERROR_LOW: 0xFF3333,
        ERROR_MED: 0xFF6600,
        ERROR_HIGH: 0xFF0000,

        // Text
        TEXT_DARK: 0x333333,
        TEXT_LIGHT: 0xFFFFFF
    }
};
